import React, {useCallback, useEffect, useMemo, useState} from 'react';

import {useGate, useStore} from 'effector-react';
import {saveAs} from 'file-saver';
import {useDropzone} from 'react-dropzone';
import {TFunction, useTranslation} from 'react-i18next';
import {generatePath, useHistory, useParams} from 'react-router-dom';

import {fetchAppealAct, fetchAppealPhoto} from 'api';
import {ReactComponent as ChevronRightIcon} from 'assets/svg/chevron-right.svg';
import {ReactComponent as DownloadIcon} from 'assets/svg/download.svg';
import {ReactComponent as UploadIcon} from 'assets/svg/upload.svg';
import {ReactComponent as ExportIcon} from 'assets/svg/upload2.svg';
import {BackTo} from 'components/common/BackTo';
import {Page} from 'components/common/Page';
import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {Icon} from 'components/ui-kit/Icon';
import {StatusWrapper} from 'components/ui-kit/PageComponents';
import {Select, SelectOption} from 'components/ui-kit/Select';
import {
    APPEAL_STATUS_COLORS,
    PRODUCT_STATUS_COLORS,
    STATUS_COLOR_LIST,
} from 'core/constants/statuses';
import {ProductStatus} from 'core/dictionaries';
import {RestError, showRestError} from 'core/errors';
import {ROUTE} from 'core/routes';
import {permissions$} from 'models/auth';
import {
    assignRequested,
    detailState$,
    Gate,
    rejectRequested,
    resolveRequested,
    responsePhotosSelected,
    takeRequested,
} from 'models/complaints/detail';
import {colors} from 'theme';
import {AppealHistoryItem, AppealPhoto, AppealStatus} from 'types';
import {formatDate} from 'utils/date';
import {handleNil} from 'utils/handleNil';
import {hasAccess} from 'utils/roles';
import {toHex} from 'utils/string';

import {SlaBadge} from '../styled';

import {
    ActionBar,
    Assignment,
    DataGrid,
    DetailCard,
    DetailTitle,
    EmptyText,
    ErrorMessage,
    Field,
    FieldLabel,
    FieldValue,
    FileDownloadButton,
    FileDownloadSpinner,
    FileList,
    FileName,
    FileRow,
    FilesSection,
    HistoryAction,
    HistoryChevron,
    HistoryContent,
    HistoryDot,
    HistoryItem,
    HistoryLine,
    HistoryList,
    HistoryMeta,
    HistoryMetaDot,
    HistoryMetaText,
    HistoryPayload,
    HistoryRail,
    HistorySection,
    HistoryStatusBadge,
    MarkingCodeActions,
    ResponseDropzone,
    ResponseDropzoneAction,
    ResponseDropzoneHint,
    ResponseDropzoneText,
    ResponseFiles,
    ResponseGrid,
    ResponseSection,
    ResponseTextarea,
    Section,
    SectionTitle,
    StatusChange,
    AssignmentsWrapper,
} from './styled';

const getEmployeeName = (employee: {
    firstName?: string;
    middleName?: string;
    lastName?: string;
    email?: string;
}) =>
    [employee.lastName, employee.firstName, employee.middleName].filter(Boolean).join(' ') ||
    employee.email ||
    '—';

const ComplaintFile = ({appealId, photo}: {appealId: number; photo: AppealPhoto}) => {
    const {t} = useTranslation();
    const [isLoading, setIsLoading] = useState(false);

    const fileName = photo.fileName || `photo-${photo.id}`;
    const download = async () => {
        try {
            setIsLoading(true);
            const file = await fetchAppealPhoto({id: appealId, photoId: photo.id});
            saveAs(file, fileName);
        } catch (error) {
            showRestError(error as RestError);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <FileRow>
            <FileName title={fileName}>{fileName}</FileName>
            <FileDownloadButton
                tooltipId={`download-photo-${photo.id}`}
                type="button"
                disabled={isLoading}
                aria-busy={isLoading}
                aria-label={t('pages.complaints.actions.downloadPhoto')}
                icon={
                    isLoading ? (
                        <FileDownloadSpinner color="current" />
                    ) : (
                        <Icon
                            source={ExportIcon}
                            width={16}
                            height={16}
                            fillColor="transparent"
                            strokeColor={colors.brandPrimary}
                        />
                    )
                }
                onClick={download}
            />
        </FileRow>
    );
};

const ResponsePhotosDropzone = ({
    remainingCount,
    onFilesSelected,
}: {
    remainingCount: number;
    onFilesSelected: (files: File[]) => void;
}) => {
    const {t} = useTranslation();
    const onDrop = useCallback(
        (acceptedFiles: File[]) => {
            const files = acceptedFiles.slice(0, remainingCount);

            if (files.length) onFilesSelected(files);
        },
        [onFilesSelected, remainingCount],
    );
    const {getInputProps, getRootProps, isDragActive} = useDropzone({
        accept: 'image/*',
        multiple: true,
        disabled: remainingCount <= 0,
        onDrop,
    });

    return (
        <ResponseDropzone isDragActive={isDragActive} {...getRootProps()}>
            <input {...getInputProps()} />
            <Icon
                source={UploadIcon}
                width={30}
                height={30}
                fillColor="transparent"
                strokeColor="#90A1B9"
            />
            <ResponseDropzoneText>
                {isDragActive ? (
                    t('pages.complaints.detail.dropHere')
                ) : (
                    <>
                        {t('pages.complaints.detail.dragAndDrop')}{' '}
                        <ResponseDropzoneAction>
                            {t('pages.complaints.detail.chooseFiles')}
                        </ResponseDropzoneAction>
                    </>
                )}
            </ResponseDropzoneText>
            <ResponseDropzoneHint>{t('pages.complaints.detail.photoHint')}</ResponseDropzoneHint>
        </ResponseDropzone>
    );
};

const handlePayloadKey = (key: string, t: TFunction) => {
    switch (key) {
        case 'assignedTo':
            return t('pages.complaints.fields.assignedTo');
        default:
            return key;
    }
};

const formatHistoryPayload = (payload: string | Record<string, unknown>, t: TFunction) => {
    if (typeof payload === 'string') return payload;

    return Object.entries(payload)
        .map(
            ([key, value]) =>
                `${handlePayloadKey(key, t)}: ${
                    typeof value === 'object' ? JSON.stringify(value) : String(value)
                }`,
        )
        .join(', ');
};

const getHistoryActionColor = (item: AppealHistoryItem) => {
    if (item.fromStatus && item.toStatus && item.fromStatus === item.toStatus) {
        return STATUS_COLOR_LIST.YELLOW;
    }

    return colors.brandPrimary;
};

const getHistoryBadgeBackground = (status: AppealStatus) => {
    switch (status) {
        case AppealStatus.SUBMITTED:
            return colors.brandLight;
        case AppealStatus.IN_PROGRESS:
            return '#FFEFD9';
        default:
            return undefined;
    }
};

export const ComplaintDetailPage = () => {
    const {t} = useTranslation();
    const history = useHistory();
    const {id: rawId} = useParams<{id: string}>();
    const id = Number(rawId);
    const state = useStore(detailState$);
    const permissions = useStore(permissions$);
    const [response, setResponse] = useState('');
    // const [actIsLoading, setActIsLoading] = useState(false);
    const [assignee, setAssignee] = useState<SelectOption<number> | null>(null);
    const [theme, setTheme] = useState<SelectOption<string> | null>(null);

    const canViewPersonnel = hasAccess(permissions, [permissions.personnelNk]);
    useGate(Gate, {id, canViewPersonnel});

    useEffect(() => setResponse(state.data?.appeal.response ?? ''), [state.data?.appeal.response]);

    const employeeOptions = useMemo(
        () =>
            state.employees.map((employee) => ({
                label: getEmployeeName(employee),
                value: employee.userId,
            })),
        [state.employees],
    );
    const themeOptions = useMemo(
        () => state.themes.map((item) => ({label: item.name, value: item.code})),
        [state.themes],
    );

    useEffect(() => {
        const assignedTo = state.data?.appeal.assignedTo;
        setAssignee(employeeOptions.find((option) => option.value === assignedTo) ?? null);
    }, [employeeOptions, state.data?.appeal.assignedTo]);

    useEffect(() => {
        const themeCode = state.data?.appeal.theme;
        setTheme(themeOptions.find((option) => option.value === themeCode) ?? null);
    }, [state.data?.appeal.theme, themeOptions]);

    if (!Number.isFinite(id)) {
        return <ErrorMessage>{t('pages.complaints.detail.notFound')}</ErrorMessage>;
    }

    const card = state.data;
    const appeal = card?.appeal;
    const restError = state.error as RestError | null;
    const isForbidden = restError?.status === 403;
    const responseIsEmpty = !response.trim();
    const responsePhotos = card?.responsePhotos ?? [];
    const markingCode = card?.markingCode;
    const isKnownProductStatus = Object.values(ProductStatus).includes(
        markingCode?.status as ProductStatus,
    );
    const codeCardPath = markingCode?.code
        ? generatePath(ROUTE.COMPLAINTS_PRODUCT_DETAIL, {
              complaintId: String(id),
              id: toHex(`null§${markingCode.code}`),
          })
        : null;
    const canUploadPhotos = Boolean(card?.canComplete && responsePhotos.length < 5);
    // const actIsAvailable =
    //     appeal?.status === AppealStatus.RESOLVED || appeal?.status === AppealStatus.REJECTED;
    // const downloadAct = async () => {
    //     try {
    //         setActIsLoading(true);
    //         const file = await fetchAppealAct(id);
    //         saveAs(file, `complaint-${id}-act.pdf`);
    //     } catch (error) {
    //         showRestError(error as RestError);
    //     } finally {
    //         setActIsLoading(false);
    //     }
    // };
    const renderStatusBadge = (status?: string | null) => {
        if (!status) return '—';

        const isKnownStatus = Object.values(AppealStatus).includes(status as AppealStatus);

        return (
            <HistoryStatusBadge
                color={
                    isKnownStatus
                        ? APPEAL_STATUS_COLORS[status as AppealStatus]
                        : STATUS_COLOR_LIST.GRAY
                }
                backgroundColor={
                    isKnownStatus ? getHistoryBadgeBackground(status as AppealStatus) : undefined
                }
            >
                {isKnownStatus ? t(`pages.complaints.statuses.${status}`) : status}
            </HistoryStatusBadge>
        );
    };

    return (
        <>
            <BackTo to={ROUTE.COMPLAINTS}>{t('pages.complaints.detail.back')}</BackTo>
            <Page title={t('pages.complaints.detail.title', {id})}>
                {isForbidden ? (
                    <ErrorMessage>{restError?.message ?? ''}</ErrorMessage>
                ) : (
                    <DetailCard
                        status={state.isLoading ? 'loading' : state.error ? 'error' : 'default'}
                    >
                        {appeal && card && (
                            <>
                                <ActionBar>
                                    <div>
                                        <DetailTitle>
                                            {t('pages.complaints.detail.title', {id: appeal.id})}
                                        </DetailTitle>
                                        <StatusWrapper color={APPEAL_STATUS_COLORS[appeal.status]}>
                                            {t(`pages.complaints.statuses.${appeal.status}`)}
                                        </StatusWrapper>
                                        {card.slaIndicator && (
                                            <SlaBadge
                                                indicator={card.slaIndicator}
                                                title={appeal.slaDueAt ?? undefined}
                                            >
                                                {t(`pages.complaints.sla.${card.slaIndicator}`)}
                                            </SlaBadge>
                                        )}
                                    </div>
                                    <div>
                                        {card.canTake && (
                                            <Button
                                                size="sm"
                                                onClick={() => takeRequested(theme?.value ?? '')}
                                                isLoading={state.actionPending}
                                            >
                                                {t('pages.complaints.actions.take')}
                                            </Button>
                                        )}
                                        {card.canComplete && (
                                            <>
                                                <Button
                                                    size="sm"
                                                    variant={ButtonVariant.Outline}
                                                    disabled={responseIsEmpty}
                                                    isLoading={state.actionPending}
                                                    onClick={() => rejectRequested(response.trim())}
                                                >
                                                    {t('pages.complaints.actions.reject')}
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    disabled={responseIsEmpty}
                                                    isLoading={state.actionPending}
                                                    onClick={() =>
                                                        resolveRequested(response.trim())
                                                    }
                                                >
                                                    {t('pages.complaints.actions.resolve')}
                                                </Button>
                                            </>
                                        )}
                                        {/* {actIsAvailable && (
                                            <Button
                                                size="sm"
                                                variant={ButtonVariant.Secondary}
                                                isLoading={actIsLoading}
                                                icon={
                                                    <Icon
                                                        source={DownloadIcon}
                                                        width={16}
                                                        height={16}
                                                    />
                                                }
                                                onClick={downloadAct}
                                            >
                                                {t('pages.complaints.actions.downloadAct')}
                                            </Button>
                                        )} */}
                                    </div>
                                </ActionBar>

                                <Assignment>
                                    <AssignmentsWrapper>
                                        <Select
                                            disabled={!card.canAssign || !canViewPersonnel}
                                            label={t('pages.complaints.fields.assignee')}
                                            isClearable
                                            options={employeeOptions}
                                            value={assignee}
                                            onChange={(option: SelectOption<number> | null) =>
                                                setAssignee(option)
                                            }
                                        />
                                        <Select
                                            disabled={!card.canTake}
                                            label={t('pages.complaints.fields.theme')}
                                            isClearable
                                            options={themeOptions}
                                            value={theme}
                                            onChange={(option: SelectOption<string> | null) =>
                                                setTheme(option)
                                            }
                                        />
                                    </AssignmentsWrapper>

                                    {card.canAssign && canViewPersonnel && (
                                        <div>
                                            <Button
                                                size="sm"
                                                disabled={!assignee?.value}
                                                isLoading={state.actionPending}
                                                onClick={() =>
                                                    assignee?.value &&
                                                    assignRequested({
                                                        assignedTo: assignee.value,
                                                    })
                                                }
                                            >
                                                {t('pages.complaints.actions.assign')}
                                            </Button>
                                        </div>
                                    )}
                                </Assignment>

                                <Section>
                                    <SectionTitle>
                                        {t('pages.complaints.detail.appeal')}
                                    </SectionTitle>
                                    <DataGrid borderBottom>
                                        {/* <Field>
                                            <FieldLabel>
                                                {t('pages.complaints.fields.theme')}
                                            </FieldLabel>
                                            <FieldValue>
                                                {handleNil(theme?.label ?? appeal.theme)}
                                            </FieldValue>
                                        </Field> */}
                                        <Field>
                                            <FieldLabel>
                                                {t('pages.complaints.detail.store')}
                                            </FieldLabel>
                                            <FieldValue>{handleNil(appeal.storeName)}</FieldValue>
                                        </Field>
                                        <Field>
                                            <FieldLabel>
                                                {t('pages.complaints.detail.address')}
                                            </FieldLabel>
                                            <FieldValue>
                                                {handleNil(appeal.storeAddress)}
                                            </FieldValue>
                                        </Field>
                                        <Field>
                                            <FieldLabel>
                                                {t('pages.complaints.detail.product')}
                                            </FieldLabel>
                                            <FieldValue>{handleNil(appeal.productName)}</FieldValue>
                                        </Field>
                                        <Field>
                                            <FieldLabel>
                                                {t('pages.complaints.detail.consumer')}
                                            </FieldLabel>
                                            <FieldValue>{handleNil(appeal.fio)}</FieldValue>
                                        </Field>
                                        <Field>
                                            <FieldLabel>
                                                {t('pages.complaints.detail.phone')}
                                            </FieldLabel>
                                            <FieldValue>{handleNil(appeal.phone)}</FieldValue>
                                        </Field>
                                    </DataGrid>
                                    <DataGrid>
                                        <Field>
                                            <FieldLabel>
                                                {t('pages.complaints.detail.description')}
                                            </FieldLabel>
                                            <FieldValue>{handleNil(appeal.description)}</FieldValue>
                                        </Field>
                                        <Field>
                                            <FieldLabel>
                                                {t('pages.complaints.fields.date')}
                                            </FieldLabel>
                                            <FieldValue>
                                                {formatDate(appeal.createdAt, 'dd.MM.yyyy HH:mm')}
                                            </FieldValue>
                                        </Field>
                                        <Field />
                                    </DataGrid>
                                </Section>

                                {!!card.complaintPhotos?.length && (
                                    <FilesSection>
                                        <SectionTitle>
                                            {t('pages.complaints.detail.photos')}
                                        </SectionTitle>
                                        <FileList>
                                            {card.complaintPhotos.map((photo) => (
                                                <ComplaintFile
                                                    key={photo.id}
                                                    appealId={id}
                                                    photo={photo}
                                                />
                                            ))}
                                        </FileList>
                                    </FilesSection>
                                )}

                                <ResponseSection>
                                    <SectionTitle>
                                        {t('pages.complaints.detail.response')}
                                    </SectionTitle>
                                    <ResponseGrid $withUpload={canUploadPhotos}>
                                        <ResponseTextarea
                                            value={response}
                                            disabled={!card.canComplete}
                                            onChange={(event) => setResponse(event.target.value)}
                                            placeholder={t(
                                                'pages.complaints.detail.responsePlaceholder',
                                            )}
                                        />
                                        {canUploadPhotos && (
                                            <ResponsePhotosDropzone
                                                remainingCount={5 - responsePhotos.length}
                                                onFilesSelected={responsePhotosSelected}
                                            />
                                        )}
                                    </ResponseGrid>
                                    {!!responsePhotos.length && (
                                        <ResponseFiles>
                                            <SectionTitle>
                                                {t('pages.complaints.detail.responsePhotos')}
                                            </SectionTitle>
                                            <FileList>
                                                {responsePhotos.map((photo) => (
                                                    <ComplaintFile
                                                        key={photo.id}
                                                        appealId={id}
                                                        photo={photo}
                                                    />
                                                ))}
                                            </FileList>
                                        </ResponseFiles>
                                    )}
                                </ResponseSection>

                                <Section>
                                    <SectionTitle>
                                        {t('pages.complaints.detail.markingCode')}
                                    </SectionTitle>
                                    <DataGrid>
                                        {markingCode?.code && (
                                            <Field>
                                                <FieldLabel>
                                                    {t('pages.complaints.detail.markingCode')}
                                                </FieldLabel>
                                                <FieldValue>
                                                    {handleNil(markingCode?.code)}
                                                </FieldValue>
                                            </Field>
                                        )}
                                        {markingCode?.status && (
                                            <Field>
                                                <FieldLabel>
                                                    {t('pages.complaints.detail.markingCodeStatus')}
                                                </FieldLabel>
                                                <FieldValue>
                                                    <StatusWrapper
                                                        color={
                                                            isKnownProductStatus
                                                                ? PRODUCT_STATUS_COLORS[
                                                                      markingCode.status as ProductStatus
                                                                  ] ?? PRODUCT_STATUS_COLORS.DEFAULT
                                                                : PRODUCT_STATUS_COLORS.DEFAULT
                                                        }
                                                    >
                                                        {isKnownProductStatus
                                                            ? t(
                                                                  `dictionaries.products.status.${markingCode.status}`,
                                                              )
                                                            : markingCode.status}
                                                    </StatusWrapper>
                                                </FieldValue>
                                            </Field>
                                        )}
                                        {markingCode?.productName && (
                                            <Field>
                                                <FieldLabel>
                                                    {t(
                                                        'pages.complaints.detail.markingCodeProduct',
                                                    )}
                                                </FieldLabel>
                                                <FieldValue>
                                                    {handleNil(markingCode.productName)}
                                                </FieldValue>
                                            </Field>
                                        )}
                                        {markingCode?.owner && (
                                            <Field>
                                                <FieldLabel>
                                                    {t('pages.complaints.detail.markingCodeOwner')}
                                                </FieldLabel>
                                                <FieldValue>
                                                    {handleNil(markingCode.owner)}
                                                </FieldValue>
                                            </Field>
                                        )}
                                    </DataGrid>
                                    {markingCode?.notFound && (
                                        <EmptyText>
                                            {t('pages.complaints.detail.codeNotFound')}
                                        </EmptyText>
                                    )}
                                    {!markingCode?.notFound && codeCardPath && (
                                        <MarkingCodeActions>
                                            <Button
                                                type="button"
                                                size="sm"
                                                variant={ButtonVariant.Secondary}
                                                onClick={() => history.push(codeCardPath)}
                                            >
                                                {t('pages.complaints.actions.openCodeCard')}
                                            </Button>
                                        </MarkingCodeActions>
                                    )}
                                </Section>

                                <HistorySection>
                                    <SectionTitle>
                                        {t('pages.complaints.detail.history')}
                                    </SectionTitle>
                                    <HistoryList>
                                        {(card.history ?? []).map((item) => (
                                            <HistoryItem key={item.id}>
                                                <HistoryRail>
                                                    <HistoryDot />
                                                    <HistoryLine />
                                                </HistoryRail>
                                                <HistoryContent>
                                                    <HistoryMeta>
                                                        <HistoryAction
                                                            $color={getHistoryActionColor(item)}
                                                        >
                                                            {handleNil(item.action)}
                                                        </HistoryAction>
                                                        <HistoryMetaText>
                                                            {formatDate(
                                                                item.createdAt,
                                                                'dd.MM.yyyy',
                                                            )}
                                                        </HistoryMetaText>
                                                        <HistoryMetaDot>·</HistoryMetaDot>
                                                        <HistoryMetaText>
                                                            {formatDate(item.createdAt, 'HH:mm')}
                                                        </HistoryMetaText>
                                                        <HistoryMetaDot>·</HistoryMetaDot>
                                                        <HistoryMetaText>
                                                            {handleNil(item.actorName)}
                                                        </HistoryMetaText>
                                                    </HistoryMeta>
                                                    {(item.fromStatus || item.toStatus) && (
                                                        <StatusChange>
                                                            {item.fromStatus &&
                                                                renderStatusBadge(item.fromStatus)}
                                                            {item.fromStatus && item.toStatus && (
                                                                <HistoryChevron>
                                                                    <Icon
                                                                        source={ChevronRightIcon}
                                                                        width={14}
                                                                        height={14}
                                                                        fillColor="transparent"
                                                                        strokeColor={
                                                                            colors.textSecondary
                                                                        }
                                                                    />
                                                                </HistoryChevron>
                                                            )}
                                                            {item.toStatus &&
                                                                renderStatusBadge(item.toStatus)}
                                                        </StatusChange>
                                                    )}
                                                    {item.payload && (
                                                        <HistoryPayload>
                                                            {formatHistoryPayload(item.payload, t)}
                                                        </HistoryPayload>
                                                    )}
                                                </HistoryContent>
                                            </HistoryItem>
                                        ))}
                                    </HistoryList>
                                </HistorySection>
                            </>
                        )}
                    </DetailCard>
                )}
            </Page>
        </>
    );
};
